import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "./data";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 md:px-8 h-28 md:h-32 flex items-center justify-between">
        <button
          onClick={() => go("#top")}
          data-testid="logo-home-button"
          className="flex items-center gap-4 group"
        >
          <span className="relative inline-flex items-center justify-center">
            {/* Breathing beacon rings */}
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full bg-[color:var(--primary)]/25 blur-2xl"
              animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0.15, 0.55] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full border border-[color:var(--primary)]/50"
              animate={{ scale: [1, 1.55], opacity: [0.55, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
            />
            <img
              src={COMPANY.logo}
              alt="Chowat Security"
              className="relative h-20 md:h-24 lg:h-28 w-auto drop-shadow-[0_0_30px_rgba(220,38,38,0.6)] transition-transform duration-300 group-hover:scale-[1.06]"
            />
          </span>
          <span className="hidden sm:block font-display text-2xl md:text-3xl leading-tight tracking-tight">
            Chowat<span className="text-[color:var(--primary-text)]">.</span>
            <span className="block text-xs md:text-sm uppercase tracking-[0.28em] text-secondary font-body">
              Security Services
            </span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-secondary hover:text-white transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[color:var(--primary)] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-call-button"
            className="hidden md:inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors"
          >
            <Phone size={15} /> {COMPANY.phone}
          </a>
          <button
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  className="text-left text-lg font-medium text-secondary hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={COMPANY.phoneHref}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] text-white font-semibold px-5 py-3 rounded-sm"
              >
                <Phone size={16} /> {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
